import React from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { withStyles } from "@material-ui/core/styles";
import TextField from "@mui/material/TextField";
import Button from "@material-ui/core/Button";
import Container from "react-bootstrap/Container";
import Link from "@material-ui/core/Link";
import CircularProgress from "@material-ui/core/CircularProgress";
import jsonwebtoken from "jsonwebtoken";
import ReactLoading from "react-loading";
import CryptoJS from "crypto-js";
import { History } from "history";
import {
  saveTokenInLocalStorageAndFillState,
  updateJobPreferencesFlag,
} from "src/redux/auth/action";
import HelpSigningIn from "./HelpSignIn";
import { signIn } from "src/services/Auth";
import config from "src/config";
import getCompanyFromDomain from "src/services/Company";
import { setCompanyFromDomain } from "src/redux/company/action";
import isGeneric from "src/services/utility";
import SignInUserVerification from "./SignInUser/SignInUserVerification";
import { apolloClient } from "src/services/GraphQL/GraphQL";
import { GET_COGNITO_USER, SEND_EMAIL_VERIFICATION } from "src/services/GraphQL/graph.query";
import Dynatrace from "src/services/Dynatrace";
import SnackbarSuccess from "src/components/common/SnackbarSuccess/SnackbarSuccess";
import { LOGIN, VERIFY_OTP } from "src/router/routes";
import Logo from "src/components/common/Header/Logo";
import { graphqlRequest as request } from "src/services/GraphQL/GraphQL";
import { getCognitoUserAttributeValue } from "src/services/CommonUtility";
import API from "src/services/Api"

type LocationState = undefined | {email: string, redirectURL: string };

const styles = () => ({});
const query = new URLSearchParams(window.location.search);
const enableEmailVerification = config.ENABLE_EMAIL_VERIFICATION === "true";
interface loginProps {
  t: (words: string) => string;
  company: {
    id: string;
  };
  redirectURL: string | null;
  pullUserInformation: (
    idToken: string,
    refreshToken: string,
    companyId: string,
    path: string
  ) => void;
  otherProps: {
    history: History;
  };
  userId: string;
  needHelpClicked: boolean;
  needHelpSignIn: () => void;
  loading: boolean;
  isTokenEmpty: string | boolean;
  updateJobPreferencesFlag: (flag: boolean) => void;
  setCompanyFromDomain: (company: any) => void;
  newUser: boolean;
}

interface loginState {
  email: string;
  isUserPresentInCognito: boolean;
  isPasswordPresent: boolean;
  phoneNumber: string;
  password: string;
  emailRequiredError: boolean;
  passwordRequiredError: boolean;
  credentialsRequiredError: boolean;
  errorPresent: boolean;
  retryCount: number;
  waitForApiResp: boolean;
  isDisabled: boolean;
  companyDetails: string;
  isEmailVerified: boolean;
  [x: number]: any;
  verifyEmailPopUp: boolean;
  emailVerificationJson: {
    companyId: string;
    email: string;
    firstName: string;
    candidateStatus: string;
    password: string;
  };
  jwtPresent: boolean;
}

class Login extends React.Component<loginProps, loginState> {
  locationState = this.props.otherProps.history.location.state as LocationState;
  emailFromLocationState = this.locationState && this.locationState.email;
  redirectURLFromLocationState = this.locationState && this.locationState.redirectURL;
  redirectPageURL=  this.props.redirectURL ?? this.redirectURLFromLocationState;

  state = {
    email: query.get("email") || this.emailFromLocationState || "",
    password: "",
    emailRequiredError: false,
    passwordRequiredError: false,
    credentialsRequiredError: false,
    errorPresent: false,
    retryCount: 0,
    waitForApiResp: false,
    isDisabled: false,
    companyDetails: "",
    isEmailVerified: true,
    verifyEmailPopUp: false,
    emailVerificationJson: {
      companyId: "",
      email: "",
      firstName: "",
      candidateStatus: "unverified",
      password: "",
    },
    jwtPresent: false,
    isPasswordPresent: false,
    isUserPresentInCognito: true,
    phoneNumber: "",
  };


  componentDidUpdate(nextProps) {
    if (this.props !== nextProps) {
      if (nextProps.isDisabled === true) {
        this.setState({
          waitForApiResp: false,
          passwordRequiredError: false,
          errorPresent: false,
          isDisabled: true,
        });
      }
    }
  }

  componentDidMount() {
    const urlParams = new URLSearchParams(window.location.search);
    let jwtToken = window.location.pathname.split("/")[2]?.split("?")[0];
    const popUpFlag = urlParams.get("verifyPopUp");
    if (popUpFlag === "true") {
      this.setState({
        verifyEmailPopUp: true,
      });
    }
    try {
      if (jwtToken) {
        const decodedTokenObject = jsonwebtoken.verify(jwtToken, config.JWT_REDIRECT_SECRET_KEY);
        if (decodedTokenObject?.password && decodedTokenObject?.email) {
          this.setState(
            {
              password: decodedTokenObject.password,
              email: decodedTokenObject.email,
              jwtPresent: true,
            },
            this.signInClickHandler
          );
        } else {
          window.open(LOGIN, "_self");
        }
      }
    } catch (e) {
      window.open(LOGIN, "_self");
    }
  }


  async getCompanyDetails() {
    let companyDetails;
    if (isGeneric()) {
      // To find company id based on user email from onBoarding
      try {
        const res = await API.get(
          `${config.API_BASE_HOST}/ui/v1/profile/searchCompanyIdByEmail?email=${this.state.email}`,
        );
        const content = res.data?.content;
        const status = res.data?.status;

        if (content.companyId) {
          companyDetails =
            content.companyId === 1 && status === "ok"
              ? config.COMPANY_CCN
              : content.companyId === 2
              ? config.COMPANY_CCA
              : config.COMPANY_CCN;

          await this.setState({ companyDetails });
        }
      } catch (e) {
        companyDetails = config.COMPANY_CCN;
        this.setState({ companyDetails });
      }
    } else {
      companyDetails = getCompanyFromDomain();
      this.setState({ companyDetails });
    }
  }

  signInClickHandler = async () => {
    try {
      let errorPresent = false;
      if (this.state.email.trim() === "") {
        this.setState({
          emailRequiredError: true,
          errorPresent: false,
        });
        errorPresent = true;
      }

      if (this.state.password.trim() === "") {
        this.setState({
          passwordRequiredError: true,
          errorPresent: false,
        });
        errorPresent = true;
      }

      if (errorPresent) return;
      this.setState({
        waitForApiResp: true,
      });
      const tokenObject = await signIn(this.state.email.trim().toLowerCase(), this.state.password);

      this.identityUserInDynatrace(tokenObject);

      await this.getCompanyDetails();
      await this.props.setCompanyFromDomain(this.state.companyDetails);

      if (!enableEmailVerification || tokenObject?.idToken?.payload?.email_verified) {
        this.setState({
          isEmailVerified: true,
        });
        this.props.pullUserInformation(
          tokenObject.accessToken.jwtToken,
          tokenObject.refreshToken.token,
          this.props.company.id,
          this.redirectPageURL!
        );
        this.props.updateJobPreferencesFlag(true);
        if (tokenObject) {
          this.setState({
            waitForApiResp: false,
          });
        }
      } else {
        this.setState({
          isEmailVerified: false,
          waitForApiResp: false,
        });

        const emailVerificationJson = {
          companyId: this.props.company.id,
          email: this.state.email.trim().toLowerCase(),
          firstName: tokenObject?.idToken?.payload?.["custom:firstName"],
          candidateStatus: "unverified",
          password: CryptoJS.AES.encrypt(
            this.state?.password?.trim(),
            config.JWT_REDIRECT_SECRET_KEY
          ).toString(),
        };

        this.setState({
          emailVerificationJson: emailVerificationJson,
        });

        apolloClient
          .mutate({
            mutation: SEND_EMAIL_VERIFICATION,
            variables: { emailVerification: emailVerificationJson },
          })
          .catch((err) => console.error(err));
      }
    } catch (err) {
      this.setState((prevState) => ({
        errorPresent: true,
        passwordRequiredError: false,
        emailRequiredError: false,
        retryCount: prevState.retryCount + 1,
        waitForApiResp: false,
        isDisabled: false,
        jwtPresent: false,
      }));
    }
  };

  handleOnChange = (event) => this.setState({ [event.target.name]: event.target.value });

  identityUserInDynatrace = (tokenObject) => {
    try {
      if (tokenObject && !Dynatrace().disable()) {
        Dynatrace().identify(this.state.email.trim());
      }
    } catch (err) {
      console.log(`Error while identifying user in dynatrace: ${err}`);
    }
  }

  handleOnPaste = (event) => {
    const pasteValue = event.clipboardData.getData("text/plain");
    if (pasteValue.includes(" ")) event.preventDefault();
  };

  handleKeyPress = (event) => {
    if (event.key === " ") event.preventDefault();
  };

  continueToSignIn = async () => {
    let errorPresent = false;
    if (this.state.email.trim() === "") {
      this.setState({
        emailRequiredError: true,
        errorPresent: false,
        isUserPresentInCognito: true
      });
      errorPresent = true;
    }
    if (errorPresent) return;
    if (!errorPresent) {
      this.setState({ emailRequiredError: false });
      this.setState({waitForApiResp: true});
      await request(GET_COGNITO_USER, {
        email: this.state.email,
      })
        .then((apiResponse) => {
          const userResponse = apiResponse?.getCognitoUser?.user;
          if (apiResponse?.getCognitoUser?.statusCode === 404) {
            this.setState({ isUserPresentInCognito: false })
          } else {
            this.setState({ isUserPresentInCognito: true })
            this.state.isPasswordPresent = getCognitoUserAttributeValue("custom:passwordPresent", userResponse?.UserAttributes) === "true";
            this.state.phoneNumber = `${getCognitoUserAttributeValue("phone_number", userResponse?.UserAttributes)}`;
            this.state.isEmailVerified = getCognitoUserAttributeValue("email_verified", userResponse?.UserAttributes) === "true";
        }
        })
        .catch((error) => {
          console.log("Error while getting user from cognito: ", error);
        }).finally(() => {
          this.setState({waitForApiResp: false});
        })

      if (this.state.isUserPresentInCognito) {
        const { email, isUserPresentInCognito, isPasswordPresent, isEmailVerified, phoneNumber } = this.state;
        const { redirectURL } = this.props
        this.props.otherProps.history.push({
          pathname: VERIFY_OTP,
          state: { email, isUserPresentInCognito, isPasswordPresent, isEmailVerified, phoneNumber, redirectURL },
        });
      }
    }
  }

  signInWithPassword = async () => {
    try {
      let errorPresent = false;
      if (this.state.password.trim() === "") {
        this.setState({
          passwordRequiredError: true,
          errorPresent: false,
        });
        errorPresent = true;
      }

      if (errorPresent) return;
      this.setState({
        waitForApiResp: true,
      });
      const tokenObject = await signIn(this.state.email.trim().toLowerCase(), this.state.password);

      this.identityUserInDynatrace(tokenObject);
      await this.getCompanyDetails();
      await this.props.setCompanyFromDomain(this.state.companyDetails);

      if (tokenObject?.idToken?.payload?.email_verified) {
        this.setState({
          isEmailVerified: true,
          waitForApiResp: false
        });
        this.props.pullUserInformation(
            tokenObject.accessToken.jwtToken,
            tokenObject.refreshToken.token,
            this.props.company.id,
            this.redirectPageURL!
        );
        this.props.updateJobPreferencesFlag(true);
      } else {
        this.setState({
          isEmailVerified: false,
          waitForApiResp: false,
        });
      }
    } catch (err) {
      this.setState((prevState) => ({
        errorPresent: true,
        passwordRequiredError: false,
        retryCount: prevState.retryCount + 1,
        waitForApiResp: false,
      }));
    } 
  }

  render() {
    const highlightHelpSignIn = (
      <Link  data-testid="forgotPasswordLink" className="needHelpHighlight" onClick={this.props.needHelpSignIn}>
        {" "}
        {this.props.t("login.helpSignInText")}{" "}
      </Link>
    );

    const grayHelpSignIn = (
      <div data-testid="forgotPasswordLink" className="needHelpTxt" onClick={this.props.needHelpSignIn} role="button">
        {" "}
        {this.props.t("login.helpSignInText")}{" "}
      </div>
    );
    const showDuplicateUserMsg =
      this.props.isTokenEmpty && !this.props.needHelpClicked && !this.props.newUser;
    
    const enablePasswordLessAuth = config.ENABLE_PASSWORD_LESS_AUTH === "true";
    const showEmailField = enablePasswordLessAuth && this.props.otherProps.history.location.pathname.includes("login");
    const showPasswordField = enablePasswordLessAuth && this.props.otherProps.history.location.pathname.includes("verify-password");

    const login = (
      <>
        {this.state.verifyEmailPopUp && (
          <span className="createPasswordSuccess">
            <SnackbarSuccess message={this.props.t("emailVerification.success")} />
          </span>
        )}
        <div className="genericLogo" />
        {showDuplicateUserMsg && (
          <div className="duplicateLoginMsgV2">{this.props.t("login.duplicateUserMessageV2")}</div>
        )}
        {!enablePasswordLessAuth && (
          <div>
            <TextField
              className={
                this.state.emailRequiredError || this.state.errorPresent
                  ? "errorMsgSignUp"
                  : "signintextfiled"
              }
              name="email"
              label={this.props.t("login.emailLabel")}
              type="Email Address"
              autoComplete="current-email"
              onChange={(event) => this.handleOnChange(event)}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              value={this.state.email}
              data-testid="LoginEmailAddress"
            />
            {this.state.emailRequiredError && (
              <div className="erroMsgsignIn">{this.props.t("login.emailError")}</div>
            )}

            <TextField
              className={
                this.state.passwordRequiredError || this.state.errorPresent
                  ? "errorMsgSignUp"
                  : "signintextfiled"
              }
              data-testid="loginPassword"
              label={this.props.t("login.passwordLabel")}
              name="password"
              type="password"
              autoComplete="current-password"
              onChange={(event) => {
                this.handleOnChange(event);
              }}
              onPaste={(event) => {
                this.handleOnPaste(event);
              }}
              onKeyPress={this.handleKeyPress}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
            {this.state.passwordRequiredError && (
              <div className="erroMsgsignIn">{this.props.t("login.passwordError")}</div>
            )}
          </div>
        )}
        {showEmailField && (
          <div>
            <TextField
              className={
                this.state.emailRequiredError || this.state.errorPresent || !this.state.isUserPresentInCognito
                  ? "errorMsgSignUp"
                  : "signintextfiled"
              }
              name="email"
              label={this.props.t("login.emailLabel")}
              type="Email Address"
              autoComplete="current-email"
              onChange={(event) => this.handleOnChange(event)}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              value={this.state.email}
              data-testid="emailAddress"
            />
            {this.state.emailRequiredError && (
              <div data-testid="emailRequiredError" className="erroMsgsignIn">{this.props.t("login.emailError")}</div>
            )}
            {(!this.state.isUserPresentInCognito) && (
              <div data-testid="userNotFoundError" className="erroMsgsignIn">{this.props.t("login.userNotFound")}</div>
            )}
            <span className="otpSignInText">{this.props.t("login.otpSignInText")}</span>
          </div>
        )}
        {showPasswordField && (
          <div>
            <TextField
              className={
                this.state.passwordRequiredError || this.state.errorPresent
                  ? "errorMsgSignUp"
                  : "signintextfiled"
              }
              data-testid="password"
              label={this.props.t("login.passwordLabel")}
              name="password"
              type="password"
              autoComplete="current-password"
              onChange={(event) => {
                this.handleOnChange(event);
              }}
              onPaste={(event) => {
                this.handleOnPaste(event);
              }}
              onKeyPress={this.handleKeyPress}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
            {this.state.passwordRequiredError && (
              <div data-testid="passwordRequiredError" className="erroMsgsignIn">{this.props.t("login.passwordError")}</div>
            )}
          </div>
        )}
        <br />
        {this.state.errorPresent && (
          <div data-testid="erroMsgsignIn" className="erroMsgsignIn">{this.props.t("login.errorMsg")}</div>
        )}

        {this.state.isDisabled === true && (
          <div className="erroMsgsignIn">{this.props.t("login.accountDisabled")}</div>
        )}

        <br />
        <div className={showPasswordField ? "d-flex align-items-end justify-content-between" : ""}>
          {showPasswordField && (
            <Button
              data-testid="loginBackButton"
              className="backButton"
              onClick={() => {
                history.back();
              }}
            >
              Back
            </Button>
          )}
          <div className={showEmailField ? "signInBtnAlign continueSignIn" : "signInBtnAlign"}>
            <Button
              data-testid="continueToSignIn"
              className={`signInButton ${showEmailField ? "continueToSignInWidth" : ""}`}
              disabled={this.state.waitForApiResp || this.props.loading}
              onClick={
                showEmailField
                  ? this.continueToSignIn
                  : showPasswordField
                  ? this.signInWithPassword
                  : this.signInClickHandler
              }
            >
              {showEmailField
                ? this.props.t("login.continueSignIn")
                : this.props.t("login.signInButton")}
              {(this.props.loading || this.state.waitForApiResp) && (
                <CircularProgress size={17} className="ml-2 loaderResendBtn" />
              )}
            </Button>
          </div>
        </div>
        <div className="signInBtnAlign">
          <Link data-testid="signUpLink" href={config.SIGN_UP_URL} className="signUpLabel">
            {" "}
            {this.props.t("login.signUpLink")}{" "}
          </Link>
          <br />
        </div>
        {!showEmailField && (
          <div className="needHelpTxt">
            {this.state.retryCount >= 3 ? highlightHelpSignIn : grayHelpSignIn}
          </div>
        )}
      </>
    );

    const needHelp = (
      <HelpSigningIn
        needHelpClicked={this.props.needHelpClicked}
        otherProps={this.props.otherProps}
      />
    );
    if (!this.state.jwtPresent) {
      return (
        <div>
          <Container>
            {!enableEmailVerification || this.state.isEmailVerified === true ? (
              this.props.needHelpClicked ? (
                needHelp
              ) : (
                login
              )
            ) : (
              <SignInUserVerification emailVerificationJson={this.state.emailVerificationJson} />
            )}
          </Container>
        </div>
      );
    } else {
      return (
        <Container fluid className="loaderPage">
          <Container>
            <div>
              <Logo />
            </div>
            <div className="loadingTxt">{this.props.t("login.loginSuccess")}</div>
            <ReactLoading type="bubbles" className="loaderColor" />
          </Container>
        </Container>
      );
    }
  }
}

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  isDisabled: state.auth.isDisabled,
  loginFailureData: state.auth.loginFailureData,
  company: state.company.company,
  userId: state.auth.userId,
  email: state.auth.email,
  loading: state.auth.loginLoading,
});

const mapDispatchToProps = (dispatch) => ({
  pullUserInformation: (accessToken, refreshToken, companyId, path) =>
    dispatch(saveTokenInLocalStorageAndFillState(accessToken, refreshToken, companyId, path)),
  updateJobPreferencesFlag: (value) => dispatch(updateJobPreferencesFlag(value)),
  setCompanyFromDomain: (company) => dispatch(setCompanyFromDomain(company)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles, { withTheme: true })(withTranslation()(Login)));
